import React from 'react';

import { invert, find, isUndefined, noop } from 'lodash';
import { elementType, bool, func, string, shape } from 'prop-types';
import { connect } from 'react-redux';
import { withState } from 'recompose';

import { bindActionCreators } from 'redux';

import * as userActions from 'actions/user';
import { NAV_LINK_MAP } from 'components/Common/LayoutV2/constants';
import withRouter from 'containers/withRouter';
import withUserType from 'containers/withUserType';
import { RouterPropTypes } from 'propTypes/RouterPropTypes';

import { UserPropTypes } from 'propTypes/UserPropTypes';
import { NOT_AUTHORIZED } from 'routes/constants';
import { isNonEmptyArray } from 'utils/arrays';

@withUserType
@withRouter
@withState('tokenValid', 'setTokenValid', false)
@connect(
  ({ user: { moduleAccess, moduleAccessLoaded } }) => ({
    moduleAccess,
    moduleAccessLoaded,
  }),
  d => bindActionCreators({ ...userActions }, d),
)
class Protected extends React.Component {
  // eslint-disable-next-line complexity
  async componentDidMount() {
    const {
      user: {
        isStudent,
        isCounselor,
        isGuardian,
        isMember,
        expiresAt,
        type,
        isLoggedIn,
        layout,
      },
      setTokenValid,
      tryRefreshingToken,
      userType,
      redirect,
      path,
      router: { navigate },
    } = this.props;

    let userSideNav;
    if (isCounselor) {
      userSideNav = layout?.k12Admin?.sideNav;
    } else if (isStudent) {
      userSideNav = layout?.student?.sideNav;
    } else if (isGuardian) {
      userSideNav = layout?.guardian?.sideNav;
    } else if (isMember) {
      userSideNav = layout?.member?.sideNav;
    }

    if (isNonEmptyArray(userSideNav)) {
      const { items } = userSideNav[0];
      const linkMap = {
        ...invert(NAV_LINK_MAP),
        // Custom pairings for some roles with different route names
        '/dashboard/student': 'side-dashboard', // For student
        '/:casefileName/:studentId/:tabId': 'side-dashboard', // For guardian
      };
      const key = linkMap[path];
      const item = find(items, { menuOptions: [{ key }] });

      let canVisitRoute;
      if (isUndefined(item)) {
        // If path prop is not in NAV_LINK_MAP, then we can also try searching using the full window.location pathname
        const { pathname } = window.location;
        const pathnameKey = linkMap[pathname];
        const newItem = find(items, { menuOptions: [{ key: pathnameKey }] });
        canVisitRoute = find(newItem?.menuOptions, {
          key: pathnameKey,
        })?.isEnabled;
      } else {
        canVisitRoute = find(item.menuOptions, {
          key,
        })?.isEnabled;
      }

      // If canVisitRoute is never explicitly set, just set to true.
      if (isUndefined(canVisitRoute)) canVisitRoute = true;

      if (!canVisitRoute) {
        navigate(NOT_AUTHORIZED);
      }
    }

    const userTypeMatches = userType ? type.includes(userType) : isLoggedIn;
    const tokenExpired = expiresAt < Date.now() / 1000;
    const { success: successfullyResetToken } = tokenExpired
      ? await tryRefreshingToken()
      : { success: true };

    const canProceed = tokenExpired
      ? userTypeMatches && successfullyResetToken
      : userTypeMatches;
    if (canProceed) {
      setTokenValid(true);
    } else if (tokenExpired && !canProceed) {
      window.location = '/logout';
    } else {
      window.location = redirect;
    }
  }

  render() {
    const {
      moduleAccessLoaded,
      checkModuleAccess,
      redirect,
      tokenValid,
      children,
    } = this.props;
    if (checkModuleAccess && !moduleAccessLoaded) {
      return null;
    }
    const { moduleAccess } = this.props;
    const hasModulesAccess = checkModuleAccess
      ? moduleAccess[checkModuleAccess]
      : true;
    if (!hasModulesAccess) {
      window.location = redirect;
      return null;
    }
    if (!tokenValid) {
      return null;
    }
    return children;
  }
}

Protected.propTypes = {
  component: elementType,
  tokenValid: bool,
  setTokenValid: func,
  userType: string,
  user: UserPropTypes,
  tryRefreshingToken: func,
  redirect: string,
  path: string,
  moduleAccess: shape({}),
  moduleAccessLoaded: bool,
  checkModuleAccess: string,
  router: RouterPropTypes,
};

Protected.defaultProps = {
  userType: null,
  redirect: '/',
  checkModuleAccess: '',
  tokenValid: false,
  setTokenValid: noop,
  user: {},
  tryRefreshingToken: noop,
  moduleAccess: {},
  moduleAccessLoaded: false,
  path: null,
  router: {},
};

export default Protected;
